import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/authContext";
import { makeAuthenticatedRequest } from "../utils/apiRequest";

const HotelListPopup = ({ city, onClose, onSelect }) => {
  const { token } = useAuth();
  const [groupedHotels, setGroupedHotels] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedHotels, setSelectedHotels] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [allHotels, setAllHotels] = useState([]);

  useEffect(() => {
    
    const fetchHotels = async () => {
      try {
        const response = await makeAuthenticatedRequest(
          "https://us-central1-evoq-travel-craft.cloudfunctions.net/get_hotel_location_data",
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ city_name: [city] }),
          }
        );
        const data = await response.json();
        const hotels = data.data.hotels || [];
        setAllHotels(hotels);

        const grouped = hotels.reduce((acc, hotel) => {
          const ratingKey = `Rating ${hotel.ratings || 0}`;
          if (!acc[ratingKey]) acc[ratingKey] = [];
          acc[ratingKey].push(hotel);
          return acc;
        }, {});

        setGroupedHotels(grouped);
      } catch (err) {
        console.error("Error fetching hotels:", err);
      } finally {
        setLoading(false);
      }
    };

    if (city) fetchHotels();
  }, [city, token]);

  const toggleHotel = (hotel) => {
    const alreadySelected = selectedHotels.find((h) => h.hotel_id === hotel.hotel_id);
    if (alreadySelected) {
      setSelectedHotels((prev) => prev.filter((h) => h.hotel_id !== hotel.hotel_id));
    } else {
      setSelectedHotels((prev) => [...prev, hotel]);
    }
  };

  const isSelected = (hotel) =>
    selectedHotels.some((h) => h.hotel_id === hotel.hotel_id);

  const handleConfirm = () => {
    if (onSelect) onSelect(selectedHotels);
    onClose();
  };

  const filterHotels = (hotels) => {
    if (!searchQuery.trim()) return hotels;
    
    const query = searchQuery.toLowerCase().trim();
    return hotels.filter(hotel => 
      hotel.name?.toLowerCase().includes(query) ||
      hotel.ratings?.toString().includes(query)
    );
  };

  const getGroupedHotels = () => {
    const filteredHotels = filterHotels(allHotels);
    return filteredHotels.reduce((acc, hotel) => {
      const ratingKey = `Rating ${hotel.ratings || 0}`;
      if (!acc[ratingKey]) acc[ratingKey] = [];
      acc[ratingKey].push(hotel);
      return acc;
    }, {});
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg p-6 shadow-lg w-96 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold">Hotels in {city}</h2>
          <button onClick={onClose} className="text-gray-600 hover:text-black">✖</button>
        </div>

        <div className="mb-4">
          <input
            type="text"
            placeholder="Search hotels by name or rating..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {selectedHotels.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {selectedHotels.map((hotel) => (
              <span
                key={hotel.hotel_id}
                className="bg-blue-100 text-blue-700 text-sm px-3 py-1 rounded-full border border-blue-300"
              >
                {hotel.name}
              </span>
            ))}
          </div>
        )}

        {loading ? (
          <p>Loading hotels...</p>
        ) : Object.keys(getGroupedHotels()).length > 0 ? (
          <div className="space-y-4">
            {Object.entries(getGroupedHotels())
              .sort((a, b) => {
                const ratingA = parseInt(a[0].replace("Rating ", ""));
                const ratingB = parseInt(b[0].replace("Rating ", ""));
                return ratingB - ratingA;
              })
              .map(([rating, hotels]) => (
                <div key={rating}>
                  <h3 className="text-md font-semibold mb-2">{rating}</h3>
                  <ul className="space-y-2">
                    {hotels.map((hotel, idx) => (
                      <li
                        key={idx}
                        onClick={() => toggleHotel(hotel)}
                        className={`p-2 border rounded cursor-pointer ${
                          isSelected(hotel)
                            ? "bg-blue-100 border-blue-400"
                            : "hover:bg-gray-100"
                        }`}
                      >
                        {hotel.name}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
          </div>
        ) : (
          <p>No hotels found.</p>
        )}

        {selectedHotels.length > 0 && (
          <div className="mt-6">
            <button
              onClick={handleConfirm}
              className="w-full py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Confirm Selection
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HotelListPopup;
